/**
 * Device Movement History Section
 * แสดงประวัติการย้ายอุปกรณ์ข้ามโครงการของสัญญาเช่า (ในหน้าดูรายละเอียดการเช่า)
 */
import React, { useState, useEffect } from 'react';
import { ArrowRightIcon, ClockIcon } from '@heroicons/react/24/outline';
import {
  RiTruckLine,
  RiCheckboxCircleLine,
  RiForbidLine,
  RiScissorsCutLine,
  RiExternalLinkLine,
  RiMoneyDollarCircleLine,
} from 'react-icons/ri';
import deviceMovementService, { MovementRow } from '../../services/deviceMovementService';
import { usePermission } from '../../hooks/usePermission';
import { formatRate, formatThaiDate } from '../../utils/movementPriceChange';

interface DeviceMovementHistorySectionProps {
  rentalId: number;
}

const DeviceMovementHistorySection: React.FC<DeviceMovementHistorySectionProps> = ({
  rentalId,
}) => {
  const [history, setHistory] = useState<MovementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { hasPermission } = usePermission();
  const canViewRental = hasPermission('rentals.view');

  // เปิด tab ใหม่เสมอ — คงหน้ารายละเอียดปัจจุบันไว้ (pattern เดียวกับ DeviceMovementCenter)
  const openRentalInNewTab = (targetRentalId: number) => {
    window.open(`/rentals/view/${targetRentalId}`, '_blank', 'noopener');
  };

  useEffect(() => {
    const loadHistory = async () => {
      if (!rentalId) return;

      setLoading(true);
      setError(null);

      try {
        const data = await deviceMovementService.getRentalHistory(rentalId);
        setHistory(data.items);
      } catch (err) {
        console.error('Error loading device movement history:', err);
        setError('ไม่สามารถโหลดประวัติการย้ายอุปกรณ์');
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [rentalId]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('th-TH', {
      year: '2-digit',
      month: 'short',
      day: 'numeric',
    });
  };

  const header = (
    <div className="px-6 py-4 border-b border-secondary-200">
      <h2 className="text-lg font-medium text-secondary-900 flex items-center">
        <RiTruckLine className="w-5 h-5 mr-2 text-secondary-500" />
        ประวัติการย้ายโครงการ
        {history.length > 0 && (
          <span className="ml-2 bg-secondary-100 text-secondary-600 text-sm px-2 py-0.5 rounded-full">
            {history.length}
          </span>
        )}
      </h2>
    </div>
  );

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-secondary-200">
        {header}
        <div className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            <span className="ml-2 text-secondary-500">กำลังโหลด...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-secondary-200">
        {header}
        <div className="p-6">
          <p className="text-danger-500 text-center">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-secondary-200">
      {header}
      <div className="p-6">
        {history.length === 0 ? (
          <div className="text-center py-8 text-secondary-500">
            <RiTruckLine className="w-12 h-12 mx-auto text-secondary-300 mb-3" />
            <p>ยังไม่มีประวัติการย้ายโครงการ</p>
          </div>
        ) : (
          <div className="space-y-4">
            {history.map((item) => (
              <div
                key={item.id}
                className="border border-secondary-200 rounded-lg p-4 hover:bg-secondary-50 transition-colors"
              >
                {/* Header: วันที่มีผล + badges */}
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="flex items-center text-sm text-secondary-500">
                    <ClockIcon className="w-4 h-4 mr-1" />
                    มีผล {formatDate(item.effective_date)}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* E2E v6 OBS-4b: ย้ายบางรายการ (split) — เดิมมีแต่ในศูนย์ย้ายอุปกรณ์
                        history endpoint คืนทั้งฝั่งต้นทาง (rental_id = เรา) และฝั่งสัญญาที่แยกออกมา
                        (new_rental_id = เรา, commit 7d97993) จึงต้องแยกป้ายตามบทบาทของสัญญานี้ */}
                    {item.is_partial && (
                      <span
                        className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full border bg-info-50 text-info-700 border-info-200"
                        title={
                          item.new_rental_id === rentalId
                            ? `สัญญานี้แยกมาจากสัญญา ${item.rental_number} (ย้ายบางรายการ)`
                            : `ย้ายบางรายการ — แยกเป็นสัญญาใหม่${item.new_rental_number ? ` ${item.new_rental_number}` : ''}`
                        }
                      >
                        <RiScissorsCutLine className="w-3.5 h-3.5 mr-1" />
                        ย้ายบางรายการ
                        {item.rental_id === rentalId && item.new_rental_id && item.new_rental_number && (
                          // SPLIT-DEL-1: สัญญาใหม่ถูกลบแล้ว → text ธรรมดา (ลิงก์จะพา 404)
                          canViewRental && !item.new_rental_is_deleted ? (
                            <button
                              type="button"
                              onClick={() => openRentalInNewTab(item.new_rental_id as number)}
                              className="ml-1 inline-flex items-center underline hover:text-info-900"
                              title="เปิดสัญญาเช่าใหม่ (แท็บใหม่)"
                            >
                              → {item.new_rental_number}
                              <RiExternalLinkLine className="w-3 h-3 ml-0.5" />
                            </button>
                          ) : (
                            <span
                              className="ml-1"
                              title={item.new_rental_is_deleted ? 'สัญญานี้ถูกลบแล้ว' : undefined}
                            >
                              → {item.new_rental_number}
                            </span>
                          )
                        )}
                      </span>
                    )}
                    {item.notice_resolved && (
                      <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full border bg-success-50 text-success-700 border-success-200">
                        <RiCheckboxCircleLine className="w-3.5 h-3.5 mr-1" />
                        รักษาลูกค้าได้ (ย้ายโครงการ)
                      </span>
                    )}
                    {item.old_site_deactivated && (
                      <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full border bg-warning-50 text-warning-700 border-warning-200">
                        <RiForbidLine className="w-3.5 h-3.5 mr-1" />
                        ปิดโครงการเดิม
                      </span>
                    )}
                  </div>
                </div>

                {/* จาก → ไป */}
                <div className="flex items-center mb-3">
                  <div className="flex-1">
                    <p className="text-sm text-secondary-500">โครงการเดิม</p>
                    <p className="font-medium text-secondary-900">{item.from_site_name || '-'}</p>
                  </div>

                  <ArrowRightIcon className="w-5 h-5 text-secondary-400 mx-4" />

                  <div className="flex-1">
                    <p className="text-sm text-secondary-500">โครงการใหม่</p>
                    <p className="font-medium text-success-600">{item.to_site_name || '-'}</p>
                  </div>
                </div>

                {/* สัญญานี้คือฝั่งที่แยกออกมา → บอกที่มา + ลิงก์กลับสัญญาต้นทาง */}
                {item.is_partial && item.new_rental_id === rentalId && (
                  <div className="mb-3 text-sm text-info-700 bg-info-50 border border-info-200 rounded-md px-3 py-2 flex items-center flex-wrap gap-1">
                    <RiScissorsCutLine className="w-4 h-4 mr-1 flex-shrink-0" />
                    <span>แยกมาจากสัญญา</span>
                    {canViewRental ? (
                      <button
                        type="button"
                        onClick={() => openRentalInNewTab(item.rental_id)}
                        className="inline-flex items-center font-medium underline hover:text-info-900"
                        title="เปิดสัญญาต้นทาง (แท็บใหม่)"
                      >
                        {item.rental_number}
                        <RiExternalLinkLine className="w-3 h-3 ml-0.5" />
                      </button>
                    ) : (
                      <span className="font-medium">{item.rental_number}</span>
                    )}
                  </div>
                )}

                {/* FB-2026-0016: การปรับราคาเช่าที่ทำพร้อมการย้ายครั้งนี้ */}
                {item.price_changes && item.price_changes.length > 0 && (
                  <div className="mb-3 bg-warning-50 border border-warning-200 rounded-md px-3 py-2" data-testid="movement-price-changes">
                    <p className="text-sm font-medium text-warning-800 flex items-center mb-1">
                      <RiMoneyDollarCircleLine className="w-4 h-4 mr-1" aria-hidden="true" />
                      ปรับราคาเช่า
                    </p>
                    <ul className="space-y-1">
                      {item.price_changes.map((pc) => (
                        <li key={pc.id} className="text-sm text-secondary-800">
                          <span className="font-medium">
                            {pc.device_name || pc.device_serial || pc.product_name || `รายการ #${pc.rental_item_id}`}
                          </span>
                          {': '}
                          ราคาเดิม {formatRate(pc.old_rate)} ถึง {formatThaiDate(pc.old_billing_end_date)}
                          {pc.old_site_name ? ` (${pc.old_site_name})` : ''}
                          {' → '}
                          ราคาใหม่ {formatRate(pc.new_rate)} เริ่ม {formatThaiDate(pc.new_billing_start_date)}
                          {pc.gap_days > 0 && (
                            <span className="ml-1 text-xs text-warning-800">(เว้น {pc.gap_days} วันไม่คิดเงิน)</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* เหตุผล */}
                <div className="mb-2">
                  <p className="text-sm text-secondary-500">เหตุผล:</p>
                  <p className="text-sm text-secondary-700">{item.reason}</p>
                </div>

                {/* ผู้ดำเนินการ + จำนวนอุปกรณ์ */}
                <div className="text-xs text-secondary-400 mt-2 pt-2 border-t border-secondary-100 flex justify-between">
                  <span>โดย: {item.moved_by_user_name || 'ไม่ระบุ'}</span>
                  <span>อุปกรณ์ {item.item_count} รายการ</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceMovementHistorySection;
